import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Button, Card, Empty, Input, Select, Skeleton, Space, Statistic,
  Table, Tag, Typography, message,
} from 'antd';
import { MoneyField, TextField } from '../../../ui/fields';
import { Field, FormModal, FormSection, V } from '../../../ui/forms';
import { PlusOutlined } from '@ant-design/icons';
import type {
  RequisitionCategory, RequisitionRow, RequisitionStatus,
} from '@velora/contracts';
import { extractApiMessage, productApi } from '../../../api';

/**
 * Admin surface — Requisitions.
 * List of open requisitions with quick status change + create + drill-in to
 * Quote Comparison (Admin #37).
 */
const CAT_LABELS: Record<RequisitionCategory, string> = {
  stationery: 'Stationery', maintenance: 'Maintenance', it: 'IT', books: 'Books',
  consumables: 'Consumables', travel: 'Travel', other: 'Other',
};
const STATUS_TONE: Record<RequisitionStatus, string> = {
  draft: 'default', submitted: 'processing', approved: 'success', rejected: 'error',
  ordered: 'processing', received: 'purple', paid: 'success', closed: 'default',
};

export function RequisitionsPage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [addOpen, setAddOpen] = useState(false);

  const list = useQuery({
    queryKey: ['requisitions', institution],
    queryFn: () => productApi.listRequisitions(institution),
    enabled: !!institution,
  });
  const update = useMutation({
    mutationFn: (v: { code: string; status: RequisitionStatus }) =>
      productApi.updateRequisitionStatus(institution, v.code, { status: v.status }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['requisitions', institution] }); },
    onError: (e) => void message.error(extractApiMessage(e) ?? 'Update failed'),
  });

  const rows = list.data?.rows ?? [];
  const by = list.data?.by_status ?? { draft: 0, submitted: 0, approved: 0, rejected: 0, ordered: 0, received: 0, paid: 0, closed: 0 };

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <Typography.Title level={3} style={{ margin: 0 }}>Requisitions</Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
            Every spend starts here. Submitted → approved → ordered → received → paid → closed.
          </Typography.Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>New requisition</Button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12 }}>
        <Card size="small"><Statistic title="Submitted" value={by.submitted} /></Card>
        <Card size="small"><Statistic title="Approved" value={by.approved} valueStyle={{ color: '#1c6442' }} /></Card>
        <Card size="small"><Statistic title="Ordered / received" value={by.ordered + by.received} /></Card>
        <Card size="small"><Statistic title="Paid / closed" value={by.paid + by.closed} /></Card>
      </div>

      <Card size="small" title={`${rows.length} requisitions`}>
        {list.isLoading && <Skeleton active paragraph={{ rows: 4 }} />}
        {rows.length === 0 && !list.isLoading && (
          <Empty description={<span>No requisitions. <a onClick={() => setAddOpen(true)}>Add one</a>.</span>} style={{ padding: 20 }} />
        )}
        {rows.length > 0 && (
          <Table<RequisitionRow>
            size="small"
            pagination={{ pageSize: 15 }}
            rowKey="id"
            dataSource={rows}
            onRow={(r) => ({ onClick: () => navigate(`/i/${institution}/admin/expenses/quotes/${r.code}`), style: { cursor: 'pointer' } })}
            columns={[
              { title: 'Code', dataIndex: 'code', key: 'c', width: 140, render: (v: string) => <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>{v}</span> },
              { title: 'Subject', dataIndex: 'subject', key: 's', render: (v: string) => <b>{v}</b> },
              { title: 'Category', dataIndex: 'category', key: 'cat', width: 130, render: (v: RequisitionCategory) => <Tag>{CAT_LABELS[v]}</Tag> },
              { title: 'Requestor', dataIndex: 'requestor_name', key: 'r', width: 140 },
              { title: 'Estimate', dataIndex: 'amount_estimate_paise', key: 'est', width: 110, align: 'right', render: (v: number) => `₹${paise(v)}` },
              {
                title: 'Best quote',
                dataIndex: 'best_quote_paise',
                key: 'best',
                width: 130,
                align: 'right',
                render: (v: number | null, r) => v != null ? <b>₹{paise(v)}</b> : <Typography.Text type="secondary">{r.quote_count} quote{r.quote_count === 1 ? '' : 's'}</Typography.Text>,
              },
              {
                title: 'Status', dataIndex: 'status', key: 'st', width: 140,
                render: (v: RequisitionStatus, r) => (
                  <Select
                    size="small"
                    value={v}
                    style={{ width: 130 }}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(next) => update.mutate({ code: r.code, status: next })}
                    options={(['draft', 'submitted', 'approved', 'rejected', 'ordered', 'received', 'paid', 'closed'] as RequisitionStatus[])
                      .map((s) => ({ value: s, label: s }))}
                  />
                ),
              },
            ]}
          />
        )}
      </Card>

      <NewRequisitionModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        institution={institution}
        onCreated={(code) => {
          setAddOpen(false);
          void qc.invalidateQueries({ queryKey: ['requisitions', institution] });
          void message.success('Requisition created');
          navigate(`/i/${institution}/admin/expenses/quotes/${code}`);
        }}
      />
    </Space>
  );
}

type RequisitionFormValues = { subject: string; category: RequisitionCategory; requestor_name: string; amount_estimate_rupees: number; note?: string };

function NewRequisitionModal({
  open, onClose, institution, onCreated,
}: { open: boolean; onClose: () => void; institution: string; onCreated: (code: string) => void }) {
  return (
    <FormModal<RequisitionFormValues, { code: string }>
      open={open} onClose={onClose}
      title="New requisition" submitText="Create"
      initial={{ category: 'other' }}
      mutation={(v) => productApi.createRequisition(institution, {
        subject: v.subject,
        category: v.category,
        requestor_name: v.requestor_name,
        amount_estimate_paise: Math.round(v.amount_estimate_rupees * 100),
        note: v.note || undefined,
      })}
      onSuccess={(r) => onCreated(r.code)}
    >
      <Field name="subject" label="Subject" required>
        <TextField placeholder="Photocopier toner · 4 boxes" autoFocus />
      </Field>
      <FormSection cols={2}>
        <Field name="category" label="Category" required>
          <Select options={(Object.keys(CAT_LABELS) as RequisitionCategory[]).map((c) => ({ value: c, label: CAT_LABELS[c] }))} />
        </Field>
        <Field name="requestor_name" label="Requestor" required>
          <TextField placeholder="A. Iyer" />
        </Field>
      </FormSection>
      <Field name="amount_estimate_rupees" label="Estimated amount" required rules={[V.money()]}>
        <MoneyField />
      </Field>
      <Field name="note" label="Note">
        <Input.TextArea rows={3} />
      </Field>
    </FormModal>
  );
}

function paise(n: number): string {
  return (n / 100).toLocaleString('en-IN', { maximumFractionDigits: 0 });
}
